/**
 * QueueState store (008 data-model, 010) — a thin reactive mirror of the
 * QueueManager's job list for the UI. The QueueManager owns lifecycle/persistence;
 * this store just holds the latest snapshot + concurrency for rendering.
 *
 * `live` (Task 3) holds the in-flight per-item progress a running job emits
 * (`onProgress` → engine `BatchProgress`), keyed by jobId. `JobRecord.items`
 * only fills at job END, so the QueuePanel's bars would sit at 0% mid-run without
 * this; it prefers `live[jobId]` while a job runs and falls back to items-derived
 * counts once the entry is cleared (bootstrap clears it on a terminal status).
 */
import { create } from "zustand";
import { currentPlatform, type JobRecord, type LocalStore } from "@genz/platform";
import type { TransparentRow } from "../transparent/run.js";
import type { ExportReport } from "../export/bundle.js";

/**
 * KV key for the operator's chosen render concurrency. DEVICE-LOCAL by design (it describes
 * THIS machine's CPU/GPU headroom, not the tenant), so it persists to `LocalStore.kv` — never
 * to the cloud — and is rehydrated on boot via `restoreConcurrency` (called from
 * createAppServices). Mirrors the `genz.batch.saveDir` pattern in `stores/batch.ts`.
 */
export const CONCURRENCY_KEY = "genz.queue.concurrency";

function concurrencyKv(store?: LocalStore) {
  return (store ?? currentPlatform()?.localStore)?.kv;
}

/** A stored concurrency is only usable when it's a finite integer ≥ 1 (the UI's own floor). */
function sanitizeConcurrency(n: unknown): number | undefined {
  return typeof n === "number" && Number.isFinite(n) && n >= 1 ? Math.floor(n) : undefined;
}

/**
 * Rehydrate the persisted concurrency into the store on boot and return it so the
 * QueueManager can be constructed with the SAME value (it used to default to 1 on every
 * launch, silently discarding the operator's choice). No-op when nothing is stored.
 */
export async function restoreConcurrency(store?: LocalStore): Promise<number | undefined> {
  const kv = concurrencyKv(store);
  if (!kv) return undefined;
  const n = sanitizeConcurrency(await kv.get<number>(CONCURRENCY_KEY));
  if (n !== undefined) useQueueStore.setState({ concurrency: n });
  return n;
}

/**
 * WS-N review-2 M-9 — the job id a Transparent RUN is remembered under: the run's ROOT job,
 * never the attempt that happens to be reporting.
 *
 * `QueueManager.retryFailed` enqueues a NEW jobId for the SAME logical run and chains it back
 * with `JobRecord.retryOf`. Keying this store by the reporting job therefore made "Retry N
 * failed" look like a different conversion: the map was REPLACED, the Transparent tab blanked,
 * and on web — where review-1 M-4 made "Download (N files)" the only save path — the converted
 * bytes of every file that had already succeeded went with it.
 *
 * The walk reads the job records this store already mirrors (`onChange` → `setJobs`, which
 * `enqueue` emits before any callback can fire), so `queue/manager.ts` stays untouched. It is
 * cycle-guarded and stops at the first ancestor the store no longer holds — the same shape as
 * the manager's own private `retryAncestors`. A job the store has never heard of is its own
 * root, which is the pre-WS-N behaviour.
 */
function runRootOf(jobs: JobRecord[], jobId: string): string {
  let cur = jobs.find((j) => j.jobId === jobId);
  const seen = new Set<string>([jobId]);
  while (cur?.retryOf && !seen.has(cur.retryOf)) {
    seen.add(cur.retryOf);
    const parent = jobs.find((j) => j.jobId === cur!.retryOf);
    if (!parent) break;
    cur = parent;
  }
  return cur?.jobId ?? jobId;
}

/** In-flight per-image progress for one running job (from engine `BatchProgress`). */
export interface LiveJobProgress {
  completed?: number;
  total?: number;
  current?: { designSlug: string; view: string };
  /**
   * Task A4: which async stage the job is in. `"preparing"` is published before the
   * `resolveMockup` await (IndexedDB + AES-GCM decrypt of baked assets) — while it sits
   * there the QueuePanel shows "Preparing assets…" instead of an idle 0% bar. It flips to
   * `"rendering"` on the first engine progress tick, when the live bar takes over.
   */
  phase?: "preparing" | "rendering";
}

interface QueueState {
  jobs: JobRecord[];
  concurrency: number;
  /** Per-job live progress while running, keyed by jobId. Cleared on a terminal status. */
  live: Record<string, LiveJobProgress>;
  setJobs(jobs: JobRecord[]): void;
  /** Set the render concurrency AND persist it device-locally (survives a restart). */
  setConcurrency(n: number): void;
  /** Record the latest per-item progress for a running job (merges — preserves `phase`). */
  setLive(jobId: string, p: LiveJobProgress): void;
  /**
   * Set a job's async `phase` (Task A4) without disturbing its progress counts. Creates a
   * phase-only entry when none exists yet — the "preparing" case fires before any progress.
   */
  setPhase(jobId: string, phase: "preparing" | "rendering"): void;
  /** Drop a job's live entry (called when the job reaches completed/failed/canceled). */
  clearLive(jobId: string): void;
  /**
   * WS-N: the per-file rows of the MOST RECENT Transparent conversion, keyed by its jobId.
   *
   * The queue's own `JobItemResult` is pixel-free and telemetry-safe by design, so it has
   * nowhere to put a roundtrip score or the converted bytes — and those are the entire point of
   * the Transparent tab's table. They live here instead of on `JobRecord`, so nothing
   * score-shaped or byte-shaped ever reaches the queue store or the telemetry payload.
   *
   * ONE job at a time on purpose: `setTransparentRows` REPLACES the map when a DIFFERENT job
   * reports, so a finished run's converted bytes are released when the next run starts instead
   * of accumulating for the session. The panel only ever shows the latest run, which is what it
   * showed before the extraction too.
   *
   * Within ONE job the rows MERGE by `source` (WS-N review-1 I-2): a paused conversion resumes
   * under the same jobId, and the port starts a fresh row array per segment, so replacing there
   * dropped the pre-pause segment from the tab's table, its "Converted N / M" and its
   * "Save all (N)".
   *
   * "One job" means one RUN: a retry reports under a new jobId but is keyed to the job it is
   * retrying (WS-N review-2 M-9 — see {@link runRootOf}), so pressing "Retry N failed" merges
   * the recovered file into the run instead of replacing the run with it.
   */
  transparentRows: Record<string, TransparentRow[]>;
  setTransparentRows(jobId: string, rows: TransparentRow[]): void;
  /**
   * WS-N review-1 I-1: the WHOLE-JOB sentence a Transparent conversion produced — "Paused —
   * kept 2 overlays; Resume continues with the rest.", "Canceled — kept …", or a job that could
   * not start at all ("Transparent conversion is not available in this build").
   *
   * It lives HERE and not in `useBatchStore.jobError` because that banner belongs to the mockup
   * run: a paused conversion used to paint it, so the Batch tab reported a failure of a render
   * that was never touched. Keyed by RUN — the root job of a retry chain, the same key the rows
   * use (WS-N review-2 M-10, see {@link runRootOf}) — so the Transparent tab shows only its own
   * run's sentence, a RETRY's sentence is not filed under an id the tab never selects, and the
   * note is cleared when the job is asked to continue.
   */
  transparentNote: Record<string, string>;
  setTransparentNote(jobId: string, note: string | null): void;
  /**
   * WS-ERR F-8 — the queue's own records are not reaching disk on this device.
   *
   * `QueueManager.persist` is called fire-and-forget from six places. A store failure used to
   * be an unhandled rejection; `5f2893b4` turned it into a job TRACE, which nothing on the
   * Queue page renders — so the operator still saw a normal row with nothing durable behind it,
   * and lost the whole run on the next restart with no idea why. This is the panel-level
   * sentence. One per session: it is a state, not a stream.
   */
  persistDegraded: string | null;
  setPersistDegraded(reason: string | null): void;
  /**
   * WS-N review-1 M-3: the Transparent tab's CURRENT run — its job id and the dated output
   * folder stamped at enqueue.
   *
   * It lives in the store because `app/Editor.tsx` UNMOUNTS the panel on a tab switch. Held as
   * component state (which it was), switching away and back showed an idle tab even though the
   * rows, the job and its status were all still here — nothing selected them. The panel's own
   * comment and `docs/frontend-flow.md` claimed the opposite; this is what makes the claim
   * true. `runFolder` rides along because losing it would send a later "Save all" into a SECOND
   * dated folder for one conversion (WS-I / D-13).
   *
   * review-1 m-2: so does `brandFolder`. The writer prefixed every overlay with
   * `spec.brandFolder`, stamped at enqueue; "Copy run to…" rebuilt those paths from the LIVE
   * Brand field, so changing the brand after a run made every re-read miss and the copy came
   * back all `missing` rows. The run's own brand is part of the run, exactly like its folder.
   */
  transparentRun: { jobId: string; runFolder?: string; brandFolder?: string } | null;
  setTransparentRun(run: { jobId: string; runFolder?: string; brandFolder?: string }): void;
  /**
   * WS-O review-1 B-1: the conversion's own `ExportReport` — what the run writer put on disk and
   * every overlay it could NOT (`failed` / `collisions` / `missing`).
   *
   * The Transparent tab had no surface for a refusal at all: `Apple/Rose.png` then
   * `Apple/rose.png` is ONE file on APFS/NTFS, the second is correctly refused, and the tab
   * showed both rows `ok` with a roundtrip score and "2 / 2 converted". The row state is honest
   * — the keying DID succeed — which is exactly why the tally has to be said out loud.
   *
   * Keyed by RUN like the rows and the note are ({@link runRootOf}), so a retry's tally lands
   * where the tab looks. A SNAPSHOT: the writer's report object is mutated per overlay and
   * released when the job goes terminal.
   */
  transparentReport: Record<string, ExportReport>;
  setTransparentReport(jobId: string, report: ExportReport): void;
}

export const useQueueStore = create<QueueState>((set) => ({
  jobs: [],
  concurrency: 1,
  live: {},
  setJobs: (jobs) => set({ jobs }),
  setConcurrency: (concurrency) => {
    set({ concurrency });
    // Best-effort persist (fire-and-forget; never blocks the UI update).
    const kv = concurrencyKv();
    if (kv) void kv.set(CONCURRENCY_KEY, concurrency);
  },
  // Merge so a "rendering" phase set alongside the same tick survives the counts update.
  setLive: (jobId, p) => set((s) => ({ live: { ...s.live, [jobId]: { ...s.live[jobId], ...p } } })),
  setPhase: (jobId, phase) =>
    set((s) => ({ live: { ...s.live, [jobId]: { ...s.live[jobId], phase } } })),
  clearLive: (jobId) =>
    set((s) => {
      if (!(jobId in s.live)) return s;
      const { [jobId]: _removed, ...rest } = s.live;
      return { live: rest };
    }),
  transparentRows: {},
  setTransparentRows: (jobId, rows) =>
    set((s) => {
      // WS-N review-2 M-9: a RETRY is the same run under a new jobId — file its rows under the
      // run's root so they MERGE with what already converted instead of replacing it.
      const runId = runRootOf(s.jobs, jobId);
      const prev = s.transparentRows[runId];
      // A DIFFERENT job REPLACES the map — see the field's doc comment: that is what frees the
      // previous run's converted bytes.
      if (!prev) return { transparentRows: { [runId]: rows } };
      // The SAME job MERGES by `source` (WS-N review-1 I-2; AR-11: an ok row's `relPath` is its
      // `.png` output, so a failed `a.jpg` and its recovered `a.png` are one row). A pause is a
      // checkpoint, not a new run: the resumed segment carries the same jobId and the
      // `runTransparent` port starts a fresh `live` array per call, so replacing dropped every
      // pre-pause row. The tab then read "Save all (1) · Converted 1 / 1" for a 3-file run — and a Save after a
      // brand change filed only the resumed segment under the new brand.
      const bySource = new Map(prev.map((r) => [r.source, r]));
      for (const r of rows) bySource.set(r.source, r);
      return { transparentRows: { [runId]: [...bySource.values()] } };
    }),
  persistDegraded: null,
  // First reason wins while one is standing (a store that is down fails on every write, and the
  // banner is a state, not a stream) — but `null` always clears, so a new session, a test, or a
  // device that recovers can put the page back. Without the clear this was a one-way latch for
  // the life of the process.
  setPersistDegraded: (persistDegraded) =>
    set((st) => (persistDegraded === null || !st.persistDegraded ? { persistDegraded } : st)),
  transparentNote: {},
  setTransparentNote: (jobId, note) =>
    set((s) => {
      // WS-N review-2 M-10: keyed to the RUN like the rows are, so a RETRY's pause/cancel
      // sentence lands where the tab looks instead of under an id nothing selects.
      const runId = runRootOf(s.jobs, jobId);
      if (note === null) {
        if (!(runId in s.transparentNote)) return s;
        const { [runId]: _removed, ...rest } = s.transparentNote;
        return { transparentNote: rest };
      }
      return { transparentNote: { ...s.transparentNote, [runId]: note } };
    }),
  transparentRun: null,
  setTransparentRun: (run) => set({ transparentRun: run }),
  transparentReport: {},
  setTransparentReport: (jobId, report) =>
    set((s) => ({ transparentReport: { [runRootOf(s.jobs, jobId)]: report } })),
}));
